import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";
import { HiOutlinePlusCircle, HiOutlineTrash, HiOutlineArrowPath, HiOutlineExclamationTriangle, HiOutlineXCircle } from "react-icons/hi2";
import { ChevronDown, Search, X } from "lucide-react";

const libraries = ["places"];

function SearchableUserSelect({ users, value, onChange, label, placeholder, required }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const isSelectedUser = (u) => u.id === value || u.correo_tecnico === value;
  const selectedUser = users.find(isSelectedUser);

  const filtered = search.trim()
    ? users.filter((u) => {
        const q = search.toLowerCase();
        return (
          u.nombre_tecnico?.toLowerCase().includes(q) ||
          u.sucursal?.toLowerCase().includes(q)
        );
      })
    : users;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (userId) => {
    onChange(userId);
    setSearch("");
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <span className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">{label}</span>}

      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
          value={value || ""}
          required
          onChange={() => {}}
        />
      )}

      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 border-[color:var(--input-border)] bg-white bg-[color:var(--input-bg)] px-3 py-2.5 text-left text-sm text-slate-800 shadow-sm outline-none ring-orange-200 transition focus:ring"
      >
        {selectedUser ? (
          <span className="truncate text-[color:var(--text-primary)]">{selectedUser.nombre_tecnico}</span>
        ) : (
          <span className="text-[color:var(--text-muted)]">{placeholder || "Seleccionar..."}</span>
        )}
        <span className="flex shrink-0 items-center gap-1 text-[color:var(--text-muted)]">
          {selectedUser && (
            <X
              size={14}
              className="cursor-pointer hover:text-rose-500"
              onClick={handleClear}
            />
          )}
          <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-[60] mt-1 w-full overflow-hidden rounded-xl border border-slate-200 border-[color:var(--surface-border)] bg-white bg-[color:var(--surface-elevated)] shadow-xl shadow-slate-200/70 shadow-[color:var(--surface-shadow)]">
          <div className="flex items-center gap-2 border-b border-slate-200 border-[color:var(--surface-border)] bg-white px-3 py-2">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o area..."
              className="w-full bg-transparent text-sm text-slate-800 text-[color:var(--text-primary)] outline-none placeholder:text-slate-400 placeholder:text-[color:var(--text-muted)]"
            />
            {search && (
              <X
                size={14}
                className="shrink-0 cursor-pointer text-[color:var(--text-muted)] hover:text-rose-500"
                onClick={() => setSearch("")}
              />
            )}
          </div>

          <ul className="max-h-56 overflow-y-auto bg-white py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-[color:var(--text-muted)]">Sin resultados</li>
            ) : (
              filtered.map((user) => (
                <li
                  key={user.id}
                  onClick={() => handleSelect(user.id)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition hover:bg-[color:var(--sidebar-hover-bg)] ${isSelectedUser(user)
                    ? "bg-[color:var(--sidebar-active-bg)]/10 font-medium text-[color:var(--btn-primary-bg)]"
                    : "text-[color:var(--text-primary)]"
                    }`}
                >
                  <span className="truncate">{user.nombre_tecnico}</span>
                  <span className="ml-auto shrink-0 truncate text-xs text-[color:var(--text-muted)]" style={{ maxWidth: "45%" }}>
                    {user.sucursal}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const EditarSolicitud = () => {
  const navigate = useNavigate();
  const { id, method } = useParams();
  const [reserva, setReserva] = useState(null);
  const [itinerarioOriginalCount, setItinerarioOriginalCount] = useState(0);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTecnico, setSelectedTecnico] = useState(null);
  const { user } = useAuth();
  const [alertaModal, setAlertaModal] = useState({ open: false, mensaje: "" });
  const autocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    status: "", empresa: "", contacto: "", equipo: "", domicilio: "", ciudad: "",
    localidad: "", telefono: "", actividades: [], itinerario: [], motivo_extension: "",
  });

  const [isOtros, setIsOtros] = useState(false);
  const [itinerario, setItinerario] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => { fetchReserva(); fetchTecnicos(); }, [id]);

  const fetchTecnicos = async () => {
    try { const response = await axios.get("https://desarrollotecnologicoar.com/api5/tecnicos"); setTecnicos(response.data); }
    catch (err) { console.error("Error al obtener los tecnicos:", err); }
  };

  const fetchReserva = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${id}`);
      const data = response.data;
      const itinerarioFormatted = data.itinerario.map((day) => ({
        id: day.id, fecha: new Date(day.fecha.split("T")[0] + "T12:00:00Z"),
        entrada: day.entrada, salida: day.salida, nuevo: false, modifico: false, id_reporte: day.id_reporte,
      }));
      setReserva(data);
      setFormData({
        status: data.status, empresa: data.empresa, contacto: data.contacto, equipo: data.equipo,
        domicilio: data.domicilio, ciudad: data.ciudad, localidad: data.localidad, telefono: data.telefono,
        actividades: data.actividades, itinerario: itinerarioFormatted, motivo_extension: data.motivo_extension || "",
      });
      setItinerario(itinerarioFormatted);
      setItinerarioOriginalCount(itinerarioFormatted.length);
    } catch (err) { console.error("Error al obtener la reserva:", err); setError("Error al obtener la reserva."); }
    finally { setLoading(false); }
  };

  const tieneReporteAsociado = (day) => {
    if (!day || day.nuevo) return false;
    if (day.id_reporte && day.id_reporte !== null) return true;
    if (!reserva?.reportes_servicio || reserva.reportes_servicio.length === 0) return false;
    return reserva.reportes_servicio.some((r) => r.itinerario_id === day.id);
  };

  const handleItinerarioChange = (index, field, value) => {
    setItinerario((prev) => { const updated = [...prev]; updated[index][field] = value; updated[index].modifico = true; return updated; });
  };

  const handleRemoveItinerario = async (index, idBuscado) => {
    if (!window.confirm("¿Estas seguro de que deseas eliminar este dia del itinerario?")) return;
    try { await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`); setItinerario((prev) => prev.filter((_, i) => i !== index)); alert("Dia eliminado correctamente."); }
    catch (error) { console.error("Error al eliminar itinerario:", error); alert("Error al eliminar el dia del itinerario."); }
  };

  const handleAddItinerario = () => {
    setItinerario((prev) => [...prev, { fecha: new Date(), entrada: "", salida: "", nuevo: true, modifico: false }]);
  };

  const handleExtensionPendiente = async (reservaId) => {
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/bono_ext/${reservaId}`, { correo_tecnico: selectedTecnico.correo_tecnico });
      alert("Bono de extension marcado como pendiente.");
    } catch (error) {
      if (error.response && error.response.status === 404) { alert("Bono no se ha marcado como pendiente."); }
      else { alert("Ocurrio un error al marcar el bono de extension."); }
    }
  };

  const condicionExtension = (reservaId, reserva) => {
    const hoy = new Date();
    const fechaReserva = new Date(reserva.itinerario[0].fecha);
    if (hoy > fechaReserva) { alert("La fecha de la reserva ya ha pasado, marcando como pendiente."); handleExtensionPendiente(reservaId); }
  };

  const getStatusOptions = () => {
    if (!reserva || !reserva.itinerario || reserva.itinerario.length === 0) {
      return { options: ["Pendiente", "Activo", "Extendido", "Finalizado", "Rechazado"], disabled: false };
    }
    const hoy = new Date();
    const fechaReserva = new Date(reserva.itinerario[0].fecha);
    if (itinerario.length > itinerarioOriginalCount && hoy >= fechaReserva) {
      return { options: ["Extendido"], disabled: true, forcedValue: "Extendido" };
    }
    return { options: ["Pendiente", "Activo", "Extendido", "Finalizado", "Rechazado"], disabled: false };
  };

  const handleAgregarNuevo = async (index) => {
    const resultado = itinerario[index];
    if (!resultado) { alert("No se encontro el itinerario."); return; }
    const nuevoItinerario = { reserva_id: id, itinerario: [{ fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida }] };
    try {
      const response = await axios.post("https://desarrollotecnologicoar.com/api5/itinerario_ticket_extension", nuevoItinerario, { headers: { "Content-Type": "application/json" } });
      const nuevoId = response.data.data[0].id;
      setItinerario((prev) => prev.map((day, i) => i === index ? { ...day, id: nuevoId, nuevo: false, modifico: false } : day));
      alert("Itinerario agregado correctamente.");
    } catch (error) { console.error("Error al agregar itinerario:", error.response?.data || error.message); alert("Error al agregar el itinerario."); }
  };

  const mostrarAlerta = (mensaje) => { setAlertaModal({ open: true, mensaje }); };

  const handleMotivoChange = (e) => {
    const val = e.target.value;
    if (val === "Otros") { setIsOtros(true); setFormData((prev) => ({ ...prev, motivo_extension: "" })); }
    else { setIsOtros(false); setFormData((prev) => ({ ...prev, motivo_extension: val })); }
  };

  const handleSubmit = async () => {
    if (!selectedTecnico) { mostrarAlerta("El campo 'Tecnico' es obligatorio para actualizar la reserva."); return; }
    if (!formData.motivo_extension?.trim()) { mostrarAlerta("Por favor ingresa un motivo"); return; }
    try {
      const updatedData = {
        ticketId: reserva.ticketid, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, numero_serie: reserva.numero_serie || "", ticket: reserva.ticket || "",
        fecha_reserva: reserva.fecha_reserva || "", hora_reserva: reserva.hora_reserva || "",
        status: formData.status || "Pendiente", correo_cliente: reserva.correo_cliente || "",
        correo_tecnico: selectedTecnico.correo_tecnico, domicilio: formData.domicilio,
        ciudad: formData.ciudad, localidad: formData.localidad, telefono: formData.telefono,
        motivo_extension: formData.motivo_extension, nombre_tecnico_asignado: selectedTecnico.nombre_tecnico,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      if (itinerario.length > itinerarioOriginalCount) { condicionExtension(id, reserva); }
      alert("Reserva actualizada correctamente.");

      const notificationPayload = { correoTecnico: selectedTecnico.correo_tecnico, ticket: reserva.ticket || "" };
      try { await axios.post("https://desarrollotecnologicoar.com/api7/notificacion_istp_extendida", notificationPayload, { headers: { "Content-Type": "application/json" } }); } catch (e) { console.error("Error notificacion:", e); }

      const agente_edito = user ? user.displayName || user.email : "";
      const emailPayload = {
        ticket: reserva.ticket || "", tecnico: selectedTecnico.correo_tecnico, empresa: formData.empresa,
        tipo_servicio: reserva.tipo_servicio || formData.equipo, correo_agente: reserva.correo_agente || reserva.correo_cliente || "",
        ticketId: reserva.ticketid || "", status: formData.status || "Activo", agente_edito, motivo_extension: formData.motivo_extension,
      };
      try { await fetch("https://script.google.com/macros/s/AKfycbyOFiQFm0Bm8UjHFBDEYI5bQGRV60VLo-i8DHB8Wx_FjixH-qV2v-p5DPQQFUcNVMLm/exec", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(emailPayload), mode: "no-cors" }); } catch (e) { console.error("Error email:", e); }

      if (method === "pendientes" || method === "aprobados") { navigate("/pendientes"); } else { navigate("/actividad"); }
    } catch (error) { console.error("Error al actualizar la reserva:", error.response?.data || error.message); alert("Error al actualizar la reserva."); }
  };

  const handleSubmitPendientes = async () => {
    if (itinerario.some((d) => d.nuevo)) {
      mostrarAlerta("Tienes dias nuevos sin guardar. Usa el botón 'Subir' en cada día nuevo antes de continuar.");
      return;
    }
    if (itinerario.some((d) => d.modifico)) {
      mostrarAlerta("Tienes dias con cambios sin guardar. Usa el botón 'Actualizar' en cada día modificado antes de continuar.");
      return;
    }
    try {
      const updatedData = {
        ticketId: reserva.ticketid, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, numero_serie: reserva.numero_serie || "", ticket: reserva.ticket || "",
        fecha_reserva: reserva.fecha_reserva || "", hora_reserva: reserva.hora_reserva || "",
        status: formData.status || "Pendiente", correo_cliente: reserva.correo_cliente || "",
        correo_tecnico: reserva.correo_tecnico || "", domicilio: formData.domicilio,
        localidad: formData.localidad, ciudad: formData.ciudad, telefono: formData.telefono,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      alert("Reserva actualizada correctamente.");
      navigate(method == "pendientes" ? "/pendientes" : "/actividad");
    } catch (error) { console.error("Error al actualizar la reserva:", error.response?.data || error.message); alert("Error al actualizar la reserva."); }
  };

  const handleItinerarioById = async (idBuscado) => {
    const resultado = itinerario.find((item) => item.id === idBuscado);
    const datosActualizados = { reserva_id: id, fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida };
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`, datosActualizados, { headers: { "Content-Type": "application/json" } });
      setItinerario((prev) => prev.map((day) => day.id === idBuscado ? { ...day, modifico: false } : day));
      alert("Fecha actualizada correctamente.");
    } catch (error) { console.error("Error al actualizar itinerario:", error); alert("Error al actualizar el itinerario."); }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.address_components) {
        const cityComponent = place.address_components.find((comp) => comp.types.includes("locality") || comp.types.includes("administrative_area_level_1"));
        setFormData({ ...formData, domicilio: place.formatted_address, ciudad: cityComponent ? cityComponent.long_name : "" });
      }
    }
  };

  const handleCancelar = () => { if (method === "pendientes" || method === "aprobados") { navigate("/pendientes"); } else { navigate("/actividad"); } };

  const handleCancelarReserva = async () => {
    const diasConReporte = itinerario.filter((day) => !day.nuevo && tieneReporteAsociado(day));
    const diasSinReporte = itinerario.filter((day) => !day.nuevo && !tieneReporteAsociado(day));
    const mensaje = diasConReporte.length > 0
      ? `Se eliminaran ${diasSinReporte.length} dia(s) del itinerario. ${diasConReporte.length} dia(s) con reporte asociado se conservaran. ¿Deseas continuar?`
      : "¿Estas seguro de que deseas cancelar esta reserva? Se eliminaran todos los dias del itinerario y el estatus cambiara a Cancelado.";
    if (!window.confirm(mensaje)) return;
    try {
      for (const day of diasSinReporte) {
        await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${day.id}`);
      }
      const updatedData = {
        ticketId: reserva.ticketid, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, numero_serie: reserva.numero_serie || "", ticket: reserva.ticket || "",
        fecha_reserva: reserva.fecha_reserva || "", hora_reserva: reserva.hora_reserva || "",
        status: "Cancelado", correo_cliente: reserva.correo_cliente || "",
        correo_tecnico: reserva.correo_tecnico || "", domicilio: formData.domicilio,
        localidad: formData.localidad, ciudad: formData.ciudad, telefono: formData.telefono,
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      alert("Reserva cancelada correctamente.");
      if (method === "pendientes" || method === "aprobados") { navigate("/pendientes"); } else { navigate("/actividad"); }
    } catch (error) {
      console.error("Error al cancelar la reserva:", error);
      alert("Error al cancelar la reserva.");
    }
  };

  if (loading) return <div className="page-container"><div className="flex items-center justify-center h-64"><svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div></div>;
  if (error) return <div className="page-container"><div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div></div>;

  const statusConfig = getStatusOptions();

  return (
    <LoadScript googleMapsApiKey="AIzaSyDHkpz3qbH2j6KuFgytJdSNEJp3Pp7XaL0" libraries={libraries}>
      <div className="page-container">
        <div className="flex items-center justify-between mb-6">
          <h1 className="page-title">Editar Reserva - Ticket #{reserva?.ticket}</h1>
          <button onClick={handleCancelarReserva} className="text-sm py-2 px-4 inline-flex items-center gap-1.5 bg-red-600 text-white rounded-btn font-semibold hover:bg-red-700 transition-colors">
            <HiOutlineXCircle className="w-4 h-4" /> Cancelar Reserva
          </button>
        </div>

        {/* Form Card */}
        <div className="card p-5 space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Empresa</label><input type="text" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} className="input" /></div>
            <div><label className="label">Contacto</label><input type="text" value={formData.contacto} onChange={(e) => setFormData({ ...formData, contacto: e.target.value })} className="input" /></div>
            <div><label className="label">Equipo</label><input type="text" value={formData.equipo} onChange={(e) => setFormData({ ...formData, equipo: e.target.value })} className="input" /></div>
            <div><label className="label">Ciudad</label><input type="text" value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} className="input" /></div>
          </div>

          <div>
            <label className="label">Domicilio</label>
            <Autocomplete onLoad={(a) => (autocompleteRef.current = a)} onPlaceChanged={handlePlaceChanged}>
              <input type="text" value={formData.domicilio} onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })} className="input" autoComplete="off" />
            </Autocomplete>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Localidad</label>
              <select value={formData.localidad} onChange={(e) => setFormData({ ...formData, localidad: e.target.value })} className="input">
                <option value="Local">Local</option>
                <option value="Foraneo">Foraneo</option>
              </select>
            </div>
            <div><label className="label">Telefono</label><input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="input" /></div>
          </div>
        </div>

        {/* Itinerario */}
        <h2 className="section-title mb-4">Itinerario</h2>
        <div className="space-y-3 mb-6">
          {itinerario.map((day, index) => {
            const tieneReporte = tieneReporteAsociado(day);
            return (
              <div key={index} className={`card p-4 ${tieneReporte ? "opacity-60" : ""}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="label">Fecha</label>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                      <DatePicker
                        value={day.fecha}
                        format="dd/MM/yyyy"
                        onChange={(v) => handleItinerarioChange(index, "fecha", v)}
                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </div>
                  <div><label className="label">Entrada</label><input type="time" value={day.entrada} onChange={(e) => handleItinerarioChange(index, "entrada", e.target.value)} className="input" /></div>
                  <div><label className="label">Salida</label><input type="time" value={day.salida} onChange={(e) => handleItinerarioChange(index, "salida", e.target.value)} className="input" /></div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {day.nuevo ? (
                    <button onClick={() => handleAgregarNuevo(index)} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1"><HiOutlinePlusCircle className="w-4 h-4" /> Subir</button>
                  ) : (
                    <>
                      {day.modifico && (
                        <button onClick={() => { if (tieneReporte) { alert("No puedes actualizar un itinerario con reporte generado."); return; } handleItinerarioById(day.id); }} disabled={tieneReporte} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50">
                          <HiOutlineArrowPath className="w-4 h-4" /> Actualizar
                        </button>
                      )}
                      <button onClick={() => { if (tieneReporte) { alert("No puedes eliminar un itinerario con reporte generado."); return; } handleRemoveItinerario(index, day.id); }} disabled={tieneReporte} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50">
                        <HiOutlineTrash className="w-4 h-4" /> Eliminar
                      </button>
                      {tieneReporte && <span className="text-xs text-slate-500 italic">Dia bloqueado (reporte #{day.id_reporte || "asociado"})</span>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button onClick={handleAddItinerario} className="btn-ghost text-sm">+ Agregar Dia</button>
          <button onClick={() => { if (method === "pendientes") { handleSubmitPendientes(); } else if (method === "aprobados") { setOpenModal(true); } }} className="btn-primary text-sm bg-emerald-600 hover:bg-emerald-700">Guardar Cambios</button>
          <button onClick={handleCancelar} className="btn-secondary text-sm">Cancelar</button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {openModal && (
        <div className="overlay" onClick={() => setOpenModal(false)}>
          <div className="modal max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Confirmar Actualizacion</h2>
            <p className="text-sm text-slate-600 mb-1">Para actualizar, confirma los siguientes datos:</p>
            <p className="text-xs text-slate-500 font-medium mb-4">NOTA* Favor de seleccionar el estatus correcto y describir el motivo.</p>

            <div className="space-y-4">
              <div>
                <label className="label">Motivo</label>
                <select value={isOtros ? "Otros" : formData.motivo_extension} onChange={handleMotivoChange} className="input">
                  <option value="">Selecciona...</option>
                  <option value="Reincidencia">Reincidencia</option>
                  <option value="Reprogramacion interna">Reprogramacion interna</option>
                  <option value="Reprogramacion externa">Reprogramacion externa</option>
                  <option value="Problemas en el equipo">Problemas en el equipo</option>
                  <option value="Recoleccion de paquetes">Recoleccion de paquetes</option>
                  <option value="Servicio inconcluso">Servicio inconcluso</option>
                  <option value="Otros">Otros...</option>
                </select>
              </div>

              {isOtros && (
                <div>
                  <label className="label">Especifique el motivo</label>
                  <textarea rows={2} placeholder="Describe aqui el motivo de la extension" value={formData.motivo_extension} onChange={(e) => setFormData((prev) => ({ ...prev, motivo_extension: e.target.value }))} className="input" />
                </div>
              )}

              <div>
                <label className="label">Estatus</label>
                <select value={statusConfig.forcedValue || formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} disabled={statusConfig.disabled} className="input disabled:opacity-60 disabled:bg-slate-100">
                  {statusConfig.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              <div>
                <label className="label">Tecnico</label>
                <SearchableUserSelect
                  users={tecnicos}
                  value={selectedTecnico?.correo_tecnico || ""}
                  onChange={(id) => {
                    if (!id) {
                      setSelectedTecnico(null);
                      return;
                    }
                    const tec = tecnicos.find((t) => t.id === id) || null;
                    setSelectedTecnico(tec);
                  }}
                  placeholder="Selecciona..."
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpenModal(false)} className="btn-secondary text-sm">Cancelar</button>
              <button onClick={() => { const pendientes = itinerario.filter((day) => day.nuevo || day.modifico); if (pendientes.length > 0) { setAlertaModal({ open: true, mensaje: `Tienes ${pendientes.length} dia(s) que no se han subido o actualizado. Por favor completa estos cambios antes de guardar.` }); return; } setOpenModal(false); handleSubmit(); }} className="btn-primary text-sm">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertaModal.open && (
        <div className="overlay" onClick={() => setAlertaModal({ ...alertaModal, open: false })}>
          <div className="modal max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <HiOutlineExclamationTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Atencion</h3>
            <p className="text-sm text-slate-600 mb-4">{alertaModal.mensaje}</p>
            <button onClick={() => setAlertaModal({ ...alertaModal, open: false })} className="btn-primary text-sm">Entendido</button>
          </div>
        </div>
      )}
    </LoadScript>
  );
};

export default EditarSolicitud;
